// src/massiveRecruitment/MassiveRecruitmentView.js
import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Chip, Divider,
  CircularProgress, Alert, Breadcrumbs, Link, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Autocomplete, TextField, LinearProgress, Tooltip, Menu, MenuItem,
  Select, FormControl, InputLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  SkipNext as SkipNextIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const ESTADOS_CONFIG = {
  borrador:  { label: 'Borrador',  color: 'default'  },
  activo:    { label: 'Activo',    color: 'success'  },
  pausado:   { label: 'Pausado',   color: 'warning'  },
  cerrado:   { label: 'Cerrado',   color: 'error'    },
  cubierto:  { label: 'Cubierto',  color: 'info'     },
  cancelado: { label: 'Cancelado', color: 'error'    },
};

const ESTADOS_OPCIONES = ['borrador', 'activo', 'pausado', 'cerrado', 'cubierto', 'cancelado'];

const PRIORIDAD_CONFIG = {
  alta:  { label: 'Alta',  color: '#ef4444' },
  media: { label: 'Media', color: '#f59e0b' },
  baja:  { label: 'Baja',  color: '#10b981' },
};

const MassiveRecruitmentView = ({ processId, onBack, onEdit }) => {
  const { t } = useTranslation();
  const { authToken, project, showAlert } = useStore();

  const [process, setProcess]                   = useState(null);
  const [allApplicants, setAllApplicants]       = useState([]); // todos del proyecto
  const [processApplicants, setProcessApplicants] = useState([]); // los del proceso (enriquecidos)
  const [loading, setLoading]                   = useState(true);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [savingStatus, setSavingStatus]         = useState(false);
  const [addingApplicant, setAddingApplicant]   = useState(false);
  const [updatingEtapa, setUpdatingEtapa]       = useState(null); // applicantId en proceso
  const [removingId, setRemovingId]             = useState(null);
  const [error, setError]                       = useState(null);

  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [filtroEtapa, setFiltroEtapa]           = useState(''); // '' = todos
  const [statusAnchor, setStatusAnchor]         = useState(null);
  const [removeDialog, setRemoveDialog]         = useState(false);
  const [applicantToRemove, setApplicantToRemove] = useState(null);

  // --- Carga de datos ---
  const loadProcess = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/massive-recruitments/${processId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data.response?.status_response === 'OK') {
        setProcess(res.data.response.data);
      } else {
        setError(t('generalApiError'));
      }
    } catch (err) {
      setError(t('generalApiError'));
      console.error('Error loading process:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProcessApplicants = async () => {
    try {
      setLoadingApplicants(true);
      const res = await api.get(`/massive-recruitments/${processId}/applicants`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data.response?.status_response === 'OK') {
        setProcessApplicants(res.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading process applicants:', err);
    } finally {
      setLoadingApplicants(false);
    }
  };

  const loadAllApplicants = async () => {
    try {
      const res = await api.get(`/applicants`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data.response?.status_response === 'OK') {
        setAllApplicants(res.data.response.data || []);
      }
    } catch (err) {
      console.error('Error loading all applicants:', err);
    }
  };

  useEffect(() => {
    if (processId) {
      loadProcess();
      loadProcessApplicants();
      loadAllApplicants();
    }
  }, [processId]);

  // --- Helpers ---
  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    try {
      const date = dateValue?._seconds
        ? new Date(dateValue._seconds * 1000)
        : new Date(dateValue);
      return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return '-'; }
  };

  // Etapas activas del proceso ordenadas
  const etapasActivas = process
    ? (process.etapas || []).filter((e) => e.activa).sort((a, b) => a.orden - b.orden)
    : [];

  // Dado un etapaId, obtener la siguiente etapa activa
  const getNextEtapa = (etapaId) => {
    const idx = etapasActivas.findIndex((e) => e.id === etapaId);
    if (idx === -1 || idx >= etapasActivas.length - 1) return null;
    return etapasActivas[idx + 1];
  };

  // Dado un etapaId, obtener el nombre
  const getEtapaNombre = (etapaId) => {
    if (!etapaId) return t('massiveRecruitment.view.sinEtapa');
    const found = etapasActivas.find((e) => e.id === etapaId);
    return found?.nombre || etapaId;
  };

  // Es la última etapa activa?
  const isLastEtapa = (etapaId) => {
    if (!etapaId || etapasActivas.length === 0) return false;
    return etapasActivas[etapasActivas.length - 1].id === etapaId;
  };

  // Postulantes disponibles para agregar (excluir los ya en el proceso)
  const processApplicantIds = new Set(processApplicants.map((a) => a.uid || a.id));
  const availableApplicants = allApplicants.filter((a) => !processApplicantIds.has(a.uid || a.id));

  // Postulantes filtrados por etapa
  const applicantsFiltrados = filtroEtapa
    ? processApplicants.filter((a) => a.etapaActual === filtroEtapa)
    : processApplicants;

  const porcentajeAvance = process
    ? Math.round(((process.vacantesContratadas ?? 0) / Math.max(process.vacantesRequeridas ?? 1, 1)) * 100)
    : 0;

  // --- Acciones ---

  const handleChangeStatus = async (newEstado) => {
    setStatusAnchor(null);
    if (newEstado === process?.estado) return;
    try {
      setSavingStatus(true);
      const res = await api.put(
        `/massive-recruitments/${processId}/status`,
        { estado: newEstado },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        setProcess((prev) => ({ ...prev, estado: newEstado }));
        showAlert(t('massiveRecruitment.view.statusUpdated'), 'success');
      } else {
        showAlert(t('generalApiError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddApplicant = async () => {
    if (!selectedApplicant) return;
    try {
      setAddingApplicant(true);
      const res = await api.post(
        `/massive-recruitments/${processId}/applicants`,
        { applicantId: selectedApplicant.uid || selectedApplicant.id },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        showAlert(t('massiveRecruitment.view.applicantAdded'), 'success');
        setSelectedApplicant(null);
        loadProcessApplicants();
      } else {
        showAlert(res.data.response?.message || t('generalApiError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
    } finally {
      setAddingApplicant(false);
    }
  };

  // Avanzar a la siguiente etapa
  const handleAvanzarEtapa = async (applicant) => {
    const nextEtapa = getNextEtapa(applicant.etapaActual);
    if (!nextEtapa) return;
    await handleSetEtapa(applicant, nextEtapa.id);
  };

  // Cambiar a una etapa específica
  const handleSetEtapa = async (applicant, etapaId) => {
    const appId = applicant.uid || applicant.id;
    try {
      setUpdatingEtapa(appId);
      const res = await api.put(
        `/massive-recruitments/${processId}/applicants/${appId}/etapa`,
        { etapaId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        // Actualizar localmente sin recargar todo
        setProcessApplicants((prev) =>
          prev.map((a) =>
            (a.uid || a.id) === appId
              ? { ...a, etapaActual: etapaId }
              : a
          )
        );
        showAlert(
          `${t('massiveRecruitment.view.etapaUpdated')}: ${getEtapaNombre(etapaId)}`,
          'success'
        );
      } else {
        showAlert(res.data.response?.message || t('generalApiError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
    } finally {
      setUpdatingEtapa(null);
    }
  };

  const handleRemoveApplicant = async () => {
    if (!applicantToRemove) return;
    const applicantId = applicantToRemove.uid || applicantToRemove.id;
    try {
      setRemovingId(applicantId);
      const res = await api.delete(
        `/massive-recruitments/${processId}/applicants/${applicantId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        showAlert(t('massiveRecruitment.view.applicantRemoved'), 'success');
        setProcessApplicants((prev) => prev.filter((a) => (a.uid || a.id) !== applicantId));
      } else {
        showAlert(t('generalApiError'), 'error');
      }
    } catch (err) {
      showAlert(t('generalApiError'), 'error');
    } finally {
      setRemovingId(null);
      setRemoveDialog(false);
      setApplicantToRemove(null);
    }
  };

  // --- Loading / Error ---
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !process) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button size="small" onClick={loadProcess}>{t('retry')}</Button>}>
          {error || t('generalApiError')}
        </Alert>
      </Box>
    );
  }

  const estadoConfig   = ESTADOS_CONFIG[process.estado] || { label: process.estado, color: 'default' };
  const prioridadConfig = PRIORIDAD_CONFIG[process.prioridad] || { label: process.prioridad, color: '#888' };

  return (
    <Box sx={{ p: 3 }}>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" variant="body1" onClick={onBack}
          sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('massiveRecruitment.title')}
        </Link>
        <Typography color="text.primary">{process.nombre}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} size="small">
            {t('back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">{process.nombre}</Typography>
          <Chip label={estadoConfig.label} color={estadoConfig.color} size="small" />
          <Chip
            label={prioridadConfig.label} size="small" variant="outlined"
            sx={{ borderColor: prioridadConfig.color, color: prioridadConfig.color }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            endIcon={savingStatus ? <CircularProgress size={14} /> : <ExpandMoreIcon />}
            onClick={(e) => setStatusAnchor(e.currentTarget)}
            disabled={savingStatus}
            size="small"
          >
            {t('massiveRecruitment.view.changeStatus')}
          </Button>
          <Menu anchorEl={statusAnchor} open={Boolean(statusAnchor)} onClose={() => setStatusAnchor(null)}>
            {ESTADOS_OPCIONES.map((estado) => (
              <MenuItem key={estado} onClick={() => handleChangeStatus(estado)} selected={estado === process.estado}>
                <Chip label={ESTADOS_CONFIG[estado]?.label || estado} color={ESTADOS_CONFIG[estado]?.color} size="small" sx={{ mr: 1 }} />
                {estado === process.estado && '✓'}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => onEdit(process)} size="small">
            {t('edit')}
          </Button>
        </Box>
      </Box>

      {/* Tarjetas de resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.view.vacantes')}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5 }}>
              {process.vacantesContratadas ?? 0}
              <Typography component="span" variant="h6" color="text.secondary"> / {process.vacantesRequeridas ?? 0}</Typography>
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(porcentajeAvance, 100)}
              sx={{ mt: 1, height: 6, borderRadius: 3 }}
              color={porcentajeAvance >= 100 ? 'success' : 'primary'}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {porcentajeAvance}% {t('massiveRecruitment.view.completado')}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.view.postulantes')}</Typography>
            <Typography variant="h4" fontWeight="bold" sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon color="primary" />
              {loadingApplicants ? <CircularProgress size={28} /> : processApplicants.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CalendarIcon fontSize="inherit" /> {t('massiveRecruitment.view.fechas')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>{t('massiveRecruitment.list.fechaInicio')}:</strong> {formatDate(process.fechaInicioBusqueda)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('massiveRecruitment.list.fechaCierre')}:</strong> {formatDate(process.fechaCierreBusqueda)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MoneyIcon fontSize="inherit" /> {t('massiveRecruitment.view.presupuesto')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>{t('massiveRecruitment.form.salarioPorPersona')}:</strong>{' '}
              {process.presupuesto?.salarioPorPersona
                ? `${Number(process.presupuesto.salarioPorPersona).toLocaleString('es-CL')} ${process.presupuesto.moneda || 'CLP'}`
                : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>{t('massiveRecruitment.view.modalidad')}:</strong> {process.modalidad || '-'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Descripción */}
      {process.descripcionProceso && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('massiveRecruitment.form.descripcion')}</Typography>
          <Typography variant="body2" color="text.secondary">{process.descripcionProceso}</Typography>
        </Paper>
      )}

      {/* Pipeline de etapas */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          {t('massiveRecruitment.view.etapas')}
        </Typography>
        {etapasActivas.length === 0 ? (
          <Alert severity="info">{t('massiveRecruitment.view.noEtapas')}</Alert>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
            {etapasActivas.map((etapa, idx) => (
              <Box key={etapa.id} sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={etapa.nombre} arrow>
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 2, py: 1,
                      display: 'flex', alignItems: 'center', gap: 1,
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50',
                      cursor: 'default',
                    }}
                  >
                    <Chip
                      label={etapa.orden} size="small" color="primary"
                      sx={{ width: 26, height: 26, borderRadius: '50%', fontSize: '0.7rem' }}
                    />
                    <Typography variant="caption" fontWeight="medium" noWrap sx={{ maxWidth: 120 }}>
                      {etapa.nombre}
                    </Typography>
                  </Paper>
                </Tooltip>
                {idx < etapasActivas.length - 1 && (
                  <ArrowForwardIcon sx={{ color: 'text.disabled', mx: 0.5, flexShrink: 0 }} fontSize="small" />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Gestión de postulantes */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="primary" />
            {t('massiveRecruitment.view.postulantesTitle')} ({processApplicants.length})
          </Typography>
          <IconButton size="small" onClick={loadProcessApplicants} disabled={loadingApplicants} title={t('refresh')}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Agregar postulante */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 3 }}>
          <Autocomplete
            options={availableApplicants}
            getOptionLabel={(o) => `${o.name}${o.email ? ' — ' + o.email : ''}`}
            value={selectedApplicant}
            onChange={(_, v) => setSelectedApplicant(v)}
            sx={{ flexGrow: 1, maxWidth: 480 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('massiveRecruitment.view.searchApplicant')}
                placeholder={t('massiveRecruitment.view.searchApplicantPlaceholder')}
                size="small"
              />
            )}
          />
          <Button
            variant="contained"
            startIcon={addingApplicant ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
            onClick={handleAddApplicant}
            disabled={!selectedApplicant || addingApplicant}
          >
            {t('massiveRecruitment.view.addApplicant')}
          </Button>
        </Box>

        {/* Filtro por etapa */}
        {etapasActivas.length > 0 && processApplicants.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('massiveRecruitment.view.filtrarPorEtapa')}</InputLabel>
              <Select
                value={filtroEtapa}
                onChange={(e) => setFiltroEtapa(e.target.value)}
                label={t('massiveRecruitment.view.filtrarPorEtapa')}
              >
                <MenuItem value="">
                  <em>{t('massiveRecruitment.view.todasLasEtapas')} ({processApplicants.length})</em>
                </MenuItem>
                {etapasActivas.map((etapa) => {
                  const count = processApplicants.filter((a) => a.etapaActual === etapa.id).length;
                  return (
                    <MenuItem key={etapa.id} value={etapa.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                        <span>{etapa.nombre}</span>
                        <Chip label={count} size="small" color={count > 0 ? 'primary' : 'default'} variant="outlined" sx={{ minWidth: 28 }} />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {filtroEtapa && (
              <Typography variant="body2" color="text.secondary">
                {applicantsFiltrados.length} {t('massiveRecruitment.view.resultados')}
              </Typography>
            )}
          </Box>
        )}

        {/* Tabla */}
        {loadingApplicants ? (
          <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
        ) : processApplicants.length === 0 ? (
          <Alert severity="info">{t('massiveRecruitment.view.noApplicants')}</Alert>
        ) : applicantsFiltrados.length === 0 ? (
          <Alert severity="warning">{t('massiveRecruitment.view.noResultadosFiltro')}</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('name')}</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>RUT / ID</strong></TableCell>
                  <TableCell><strong>{t('massiveRecruitment.view.pais')}</strong></TableCell>
                  <TableCell><strong>{t('massiveRecruitment.view.etapaActual')}</strong></TableCell>
                  <TableCell><strong>{t('massiveRecruitment.view.cambiarEtapa')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('actions')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applicantsFiltrados.map((applicant) => {
                  const appId      = applicant.uid || applicant.id;
                  const isUpdating = updatingEtapa === appId;
                  const isRemoving = removingId === appId;
                  const nextEtapa  = getNextEtapa(applicant.etapaActual);
                  const esUltima   = isLastEtapa(applicant.etapaActual);

                  return (
                    <TableRow key={appId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{applicant.name || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{applicant.email || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {applicant.rut || applicant.identifier || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {applicant.country || '-'}
                        </Typography>
                      </TableCell>

                      {/* Etapa actual */}
                      <TableCell>
                        {applicant.etapaActual ? (
                          <Chip
                            label={getEtapaNombre(applicant.etapaActual)}
                            size="small"
                            color={esUltima ? 'success' : 'primary'}
                            variant={esUltima ? 'filled' : 'outlined'}
                          />
                        ) : (
                          <Chip label={t('massiveRecruitment.view.sinEtapa')} size="small" color="default" variant="outlined" />
                        )}
                      </TableCell>

                      {/* Selector de etapa + botón avanzar */}
                      <TableCell sx={{ minWidth: 260 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Selector de cualquier etapa */}
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={applicant.etapaActual || ''}
                              onChange={(e) => handleSetEtapa(applicant, e.target.value)}
                              disabled={isUpdating || etapasActivas.length === 0}
                              displayEmpty
                            >
                              {etapasActivas.length === 0 && (
                                <MenuItem value="">—</MenuItem>
                              )}
                              {etapasActivas.map((etapa) => (
                                <MenuItem key={etapa.id} value={etapa.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {etapa.orden}.
                                    </Typography>
                                    {etapa.nombre}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {/* Botón avanzar */}
                          <Tooltip
                            title={nextEtapa
                              ? `${t('massiveRecruitment.view.avanzarA')}: ${nextEtapa.nombre}`
                              : t('massiveRecruitment.view.ultimaEtapa')}
                            arrow
                          >
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => handleAvanzarEtapa(applicant)}
                                disabled={!nextEtapa || isUpdating}
                                startIcon={isUpdating
                                  ? <CircularProgress size={14} color="inherit" />
                                  : <SkipNextIcon />}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {esUltima
                                  ? t('massiveRecruitment.view.ultimaEtapa')
                                  : t('massiveRecruitment.view.avanzar')}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      {/* Acciones */}
                      <TableCell align="right">
                        <Tooltip title={t('massiveRecruitment.view.removeApplicant')}>
                          <span>
                            <IconButton
                              size="small" color="error"
                              disabled={isRemoving}
                              onClick={() => { setApplicantToRemove(applicant); setRemoveDialog(true); }}
                            >
                              {isRemoving ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Logística */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon color="action" />
          {t('massiveRecruitment.form.tabs.logistics')}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.form.tipoContrato')}</Typography>
            <Typography variant="body2">{process.tipoContrato || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.form.jornada')}</Typography>
            <Typography variant="body2">{process.jornada || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.form.horario')}</Typography>
            <Typography variant="body2">{process.condicionesLaborales?.horario || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.form.ubicacion')}</Typography>
            <Typography variant="body2">
              {[process.ubicacion?.comuna, process.ubicacion?.ciudad, process.ubicacion?.pais]
                .filter(Boolean).join(', ') || '-'}
            </Typography>
          </Grid>
          {process.canalesPublicacion?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">{t('massiveRecruitment.form.canalesPublicacion')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {process.canalesPublicacion.map((canal) => (
                  <Chip key={canal} label={t(`massiveRecruitment.form.canales.${canal}`)} size="small" variant="outlined" />
                ))}
              </Box>
            </Grid>
          )}
          {process.aceptaExtranjeros && (
            <Grid item xs={12}>
              <Chip icon={<CheckCircleIcon />} label={t('massiveRecruitment.form.aceptaExtranjeros')}
                color="success" variant="outlined" size="small" />
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Dialog confirmar quitar postulante */}
      <Dialog open={removeDialog} onClose={() => setRemoveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('massiveRecruitment.view.removeApplicantTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('massiveRecruitment.view.removeApplicantConfirm')}{' '}
            <strong>"{applicantToRemove?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('massiveRecruitment.view.removeApplicantNote')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialog(false)} color="inherit">{t('cancel')}</Button>
          <Button onClick={handleRemoveApplicant} color="error" variant="contained" disabled={!!removingId}>
            {removingId ? <CircularProgress size={20} /> : t('remove')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MassiveRecruitmentView;